import React from "react";
import { EVENT_SOURCE_URL, getAuthorizationHeader, V2 } from "../Api";

import { Callback, EventSourcePolyfill } from "event-source-polyfill";
import withAuth from "./withAuth";
import { IAuthProps } from "./AuthContext";

export type EventStreamListener = {
    id: string,
    handler: (data: any, eventId: string) => void;
};

interface IEventStreamProps {
    channels: Array<EventStreamListener>;
    debugId?: string;
}

interface IEventStreamContext {
    attachEvent(event: string, cb: Callback): Promise<void>;
    removeEvent(event: string, cb: Callback): Promise<void>;
}

const EventStreamContext = React.createContext<IEventStreamContext>({
    attachEvent: () => Promise.reject(),
    removeEvent: () => Promise.reject(),
});

/*
export const EventStreamSource1 = React.memo((props: React.PropsWithChildren<{}>) => {
    useEffect(() => {
        if (!EventSourcePolyfill) {
            console.warn("EventSource is not supported by this browser. Real-time updates are disabled");
            return;
        }

        const eventSource = new EventSourcePolyfill(EVENT_SOURCE_URL, {
            headers: {
                "Authorization": getAuthorizationHeader()
            }
        });

        return () => {
            // Clean up listener
            eventSource.close();
        };
    }, []);

    return <EventStreamContext.Provider value={EventStreamValue}>
        {props.children}
    </EventStreamContext.Provider>;
});
*/

class EventStreamSourceInner extends React.PureComponent<IAuthProps> {
    private _eventSource?: EventSourcePolyfill;
    private _connectionId: string = "";
    private _listenedEvents: {
        [id: string]: number
    } = {};

    constructor(props: IAuthProps) {
        super(props);

        this.createEventSource();

        this.addListener = this.addListener.bind(this);
        this.removeListener = this.removeListener.bind(this);
    }

    public componentWillUnmount() {
        if (this._eventSource) {
            this._connectionId = "";
            this._eventSource.close();
        }
    }

    public componentDidUpdate(prevProps: Readonly<IAuthProps>) {
        if (this.props.authContext.isAuthenticated) {
            this.createEventSource();
        }
    }

    public render() {
        const ctx: IEventStreamContext = {
            attachEvent: this.addListener,
            removeEvent: this.removeListener,
        };

        return <EventStreamContext.Provider value={ctx}>
            {this.props.children}
        </EventStreamContext.Provider>;
    }

    private async addListener(event: string, cb: Callback) {
        this._eventSource?.addEventListener(event, cb);

        if (!this._listenedEvents[event]) {
            // Subscribe to event
            const update = {
                addEvents: [event]
            };

            if (this._connectionId) {
                try {
                    await V2.patch(`event-stream/${this._connectionId}`, update);
                }
                catch (e) {
                    if (e.statusCode === 404) {
                        // Connection could not be found. This should not be a big deal
                        //   because we were removing listeners anyway
                        this.reset();
                    }
                }
            }

            this._listenedEvents[event] = 0;
        }

        this._listenedEvents[event]++;
    }

    private async removeListener(event: string, cb: Callback) {
        this._eventSource?.removeEventListener(event, cb);

        this._listenedEvents[event]--;
        if (!this._listenedEvents[event]) {
            // Unsubscribe from event
            const update = {
                removeEvents: [event]
            };

            if (this._connectionId) {
                try {
                    await V2.patch(`event-stream/${this._connectionId}`, update);
                }
                catch (e) {
                    if (e.statusCode === 404) {
                        // Connection could not be found. This should not be a big deal
                        //   because we were removing listeners anyway
                        this.reset();
                    }
                }
            }

            delete this._listenedEvents[event];
        }
    }

    private createEventSource() {
        if (!getAuthorizationHeader()) {
            return;
        }

        if (!EventSourcePolyfill) {
            console.warn("EventSource is not supported by this browser. Real-time updates are disabled");
            return;
        }

        this._eventSource = new EventSourcePolyfill(EVENT_SOURCE_URL, {
            headers: {
                "Authorization": getAuthorizationHeader()
            }
        });
        this._eventSource.addEventListener("init", (data) => {
            this._connectionId = data.data;
            console.log("EventStream ID", this._connectionId);

            // Re-subscribe to events
            const events = Object.keys(this._listenedEvents);
            if (events.length > 0) {
                const update = {
                    addEvents: events
                };

                for (const evt of events) {
                    console.log("Subscribe from init", evt);
                }

                V2.patch<void>(`event-stream/${this._connectionId}`, update).catch(e => {
                    // Something went wrong
                    if (e.statusCode === 404) {
                        // Our connection could not be found??
                        this.reset();
                    }
                });
            }
        });
    }

    private reset() {
        this._eventSource?.close();
        this._connectionId = "";

        // Try again
        this.createEventSource();
    }
}

export const EventStreamSource = withAuth(EventStreamSourceInner);

class EventStream extends React.PureComponent<IEventStreamProps> {
    static contextType = EventStreamContext;

    public componentDidMount() {
        for (const channel of this.props.channels) {
            console.log("Subscribe from mount", channel.id, this.props.debugId);
            this.context.attachEvent(channel.id, channel.handler);
        }
    }

    public componentWillUnmount() {
        for (const item of this.props.channels) {
            console.log("Unsubscribe from unmount", item.id, this.props.debugId);
            this.context.removeEvent(item.id, item.handler);
        }
    }

    public componentDidUpdate(prevProps: Readonly<IEventStreamProps>) {
        if (this.props.channels !== prevProps.channels) {
            const curChannels = this.props.channels.map(a => a.id).join();
            const prevChannels = prevProps.channels.map(a => a.id).join();

            if (curChannels !== prevChannels) {
                // Channels changed
                for (const item of this.props.channels) {
                    if (!prevProps.channels.some(a => a.id === item.id)) {
                        // New channel
                        console.log("Subscribe from update", item.id, this.props.debugId);
                        this.context.attachEvent(item.id, item.handler);
                    }
                }

                for (const item of prevProps.channels) {
                    if (!this.props.channels.some(a => a.id === item.id)) {
                        // Remove channel
                        console.log("Unsubscribe from update", item.id, this.props.debugId);
                        this.context.removeEvent(item.id, item.handler);
                    }
                }
            }
        }
    }

    public render() {
        return null;
    }
}

export default EventStream;
